import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { LuCalendarDays, LuCalendarX, LuPlus } from 'react-icons/lu';
// Only global theme variables are left in this file; importing it keeps their place in the cascade
import './VisitsModal.module.css';
import Button from '../../components/ui/Button';
import GlassIcon from '../../components/ui/GlassIcon';
import Modal from '../../components/ui/Modal';
import { db } from '../../config/firebaseConfig';
import { useTranslation } from '../../i18n/LanguageContext';
import AddVisitModal from './AddVisitModal';

const HEAD = 'tw-sticky tw-top-0 tw-z-[1] tw-min-w-0 tw-whitespace-nowrap tw-border-b tw-border-slate-200 tw-bg-slate-50 tw-px-3 tw-py-2.5 tw-text-left tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-[0.08em] tw-text-slate-500 dark:tw-border-white/10 dark:tw-bg-[#262a34] dark:tw-text-slate-400';
const CELL = 'tw-whitespace-nowrap tw-border-b tw-border-slate-100 tw-px-3 tw-py-2.5 tw-align-middle dark:tw-border-white/[0.06]';

/** "Ziyaretler": the project's visits with the staff member's name; adds new ones. */
const VisitsModal = ({ project, onClose }) => {
  const { t, language } = useTranslation();
  const tRef = useRef(t);
  tRef.current = t;
  const [visits, setVisits] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchVisits = useCallback(async () => {
    setLoading(true);
    try {
      const snapshot = await getDocs(query(collection(db, 'Visits'), where('projectId', '==', project.id)));
      // Her ziyaretin çalışan adı Employees'tan okunur
      const visitsData = await Promise.all(snapshot.docs.map(async (visitDoc) => {
        const visitData = visitDoc.data();
        let employeeFullName = 'N/A';
        if (visitData.employeeId) {
          const employeeDoc = await getDoc(doc(db, 'Employees', visitData.employeeId));
          if (employeeDoc.exists()) {
            const employeeData = employeeDoc.data();
            employeeFullName = `${employeeData.eName} ${employeeData.eSurname}`;
          }
        }
        return { id: visitDoc.id, ...visitData, employeeFullName };
      }));
      setVisits(visitsData);
    } catch (error) {
      console.error('Error fetching visits:', error);
      toast.error(tRef.current('projectManagement.visits.loadError'));
    } finally {
      setLoading(false);
    }
  }, [project.id]);

  useEffect(() => {
    fetchVisits();
  }, [fetchVisits]);

  let body;
  if (loading) {
    body = (
      <div aria-label={t('projectManagement.visits.loading')} className="tw-space-y-2">
        {[0, 1, 2, 3].map((index) => <span key={index} className="tw-block tw-h-10 tw-animate-pulse tw-rounded-xl tw-bg-slate-200/70 dark:tw-bg-white/[0.06]" />)}
      </div>
    );
  } else if (visits.length === 0) {
    body = (
      <div className="tw-flex tw-flex-col tw-items-center tw-gap-3 tw-px-6 tw-py-10 tw-text-center">
        <span className="tw-grid tw-h-14 tw-w-14 tw-place-items-center tw-rounded-2xl tw-bg-slate-100 tw-text-slate-400 dark:tw-bg-white/[0.05] dark:tw-text-slate-500">
          <LuCalendarX aria-hidden="true" className="tw-h-7 tw-w-7" />
        </span>
        <p className="tw-text-sm tw-font-semibold tw-text-slate-700 dark:tw-text-slate-200">{t('projectManagement.visits.empty')}</p>
      </div>
    );
  } else {
    body = (
      <div className="tw-max-h-[55vh] tw-overflow-auto tw-rounded-2xl tw-border tw-border-slate-200 glass-scrollbar dark:tw-border-white/10">
        <table className="tw-w-full tw-border-separate tw-border-spacing-0 tw-text-sm">
          <thead className="tw-bg-transparent">
            <tr className="tw-bg-transparent">
              <th scope="col" className={HEAD}>{t('projectManagement.visits.employee')}</th>
              <th scope="col" className={HEAD}>{t('projectManagement.visits.firstVisitDate')}</th>
              <th scope="col" className={HEAD}>{t('projectManagement.visits.period')}</th>
              <th scope="col" className={HEAD}>{t('projectManagement.visits.patientId')}</th>
              <th scope="col" className={HEAD}>{t('projectManagement.visits.visitNumber')}</th>
            </tr>
          </thead>
          <tbody className="tw-bg-transparent">
            {visits.map((visit) => (
              <tr key={visit.id} className="tw-bg-transparent tw-text-slate-700 dark:tw-text-slate-200">
                <td className={`${CELL} tw-font-medium tw-text-slate-900 dark:tw-text-white`}>{visit.employeeFullName}</td>
                <td className={`${CELL} tw-tabular-nums`}>{visit.firstVisitDate || '-'}</td>
                <td className={`${CELL} tw-tabular-nums`}>{visit.period || '-'}</td>
                <td className={`${CELL} tw-tabular-nums`}>{visit.patientId || '-'}</td>
                <td className={`${CELL} tw-tabular-nums`}>{visit.visitNumber || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <>
      <Modal
        isOpen
        onClose={onClose}
        size="lg"
        title={t('projectManagement.visits.title')}
        subtitle={loading ? project.titleShort : `${project.titleShort || ''} · ${t('projectManagement.visits.count', { count: visits.length })}`}
        closeLabel={t('common.close')}
        lang={language}
        icon={(
          <GlassIcon gradient="tw-from-indigo-400 tw-to-blue-600 tw-shadow-indigo-500/30" size="xs">
            <LuCalendarDays aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
          </GlassIcon>
        )}
        actions={(
          <Button variant="primary" size="sm" onClick={() => setShowAddModal(true)} className="max-sm:tw-w-full">
            <LuPlus aria-hidden="true" className="tw-h-4 tw-w-4" />
            {t('projectManagement.visits.add')}
          </Button>
        )}
        footer={<Button variant="ghost" onClick={onClose}>{t('common.close')}</Button>}
      >
        {body}
      </Modal>

      {showAddModal && (
        <AddVisitModal project={project} onClose={() => setShowAddModal(false)} onVisitAdded={fetchVisits} />
      )}
    </>
  );
};

VisitsModal.propTypes = {
  project: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default VisitsModal;
